import { t } from "@lingui/core/macro";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import type { SendCommitRequest } from "@/api";
import { DynamicForm } from "@/components/forms/DynamicForm";
import { RampsText } from "@/components/ramps/Text";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useNavigateBack } from "@/hooks/useNavigateBack";
import { useAnalytics } from "@/hooks/usePostHog";
import { navigateWithRampsState } from "@/lib/ramps-navigation";
import { getAuthTokenFromSearchParams } from "@/lib/ramps-search-schema";
import { toast } from "@/lib/toast";
import { rampsActions, useRampsStore } from "@/stores/use-ramps-store";
import type { GFFPFieldInfo } from "@/types/additional-info.models";
import { Progress } from "../components/ui/progress";

function AdditionalInfoPage() {
	const search = Route.useSearch();
	const navigate = useNavigate();
	const store = useRampsStore();
	const handleBack = useNavigateBack();
	const { trackPageView } = useAnalytics();
	// Get auth token (supports both v2 and v5 API)
	const authToken = getAuthTokenFromSearchParams(search) || store.token;

	// Track page view
	useEffect(() => {
		trackPageView("additional-info", { page_name: "additional-info" });
	}, [trackPageView]);
	const { apiClient } = useApiClientWithAutoVersion({
		authToken,
	});

	// Extract fields once
	const offendingFields =
		(store.transactionResponse?.errors?.[0]
			?.offendingFields as GFFPFieldInfo[]) || [];

	const formatFieldsWithDotNotation = (fields: Record<string, unknown>) => {
		const result: Record<string, unknown> = {};

		for (const [key, value] of Object.entries(fields)) {
			// Convert dashes to dots in the key
			const dotNotationKey = key.replace(/-/g, ".");
			result[dotNotationKey] = value;
		}

		return result;
	};

	const formatFieldsToDeeplyNested = (formData: Record<string, unknown>) => {
		const dotNotatedData = formatFieldsWithDotNotation(formData);
		const result: Record<string, unknown> = {};

		for (const [key, value] of Object.entries(dotNotatedData)) {
			// Split the key by dots to create the nested path
			const keys = key.split(".");
			let current = result;

			// Navigate/create the nested structure
			for (let i = 0; i < keys.length - 1; i++) {
				const currentKey = keys[i];
				if (!(currentKey in current)) {
					current[currentKey] = {};
				}
				current = current[currentKey] as Record<string, unknown>;
			}

			// Set the final value
			const finalKey = keys[keys.length - 1];
			current[finalKey] = value;
		}

		return result;
	};

	const deepMerge = (
		target: Record<string, unknown>,
		source: Record<string, unknown>,
	): Record<string, unknown> => {
		const result = { ...target };

		for (const [key, value] of Object.entries(source)) {
			if (value && typeof value === "object" && !Array.isArray(value)) {
				// If both target and source have objects at this key, merge them recursively
				if (
					result[key] &&
					typeof result[key] === "object" &&
					!Array.isArray(result[key])
				) {
					result[key] = deepMerge(
						result[key] as Record<string, unknown>,
						value as Record<string, unknown>,
					);
				} else {
					// Otherwise, use the source value
					result[key] = value;
				}
			} else {
				// For primitive values, arrays, or null, use the source value
				result[key] = value;
			}
		}

		return result;
	};

	const addAdditionalFieldsToValidatePayload = (
		formattedFormData: Record<string, unknown>,
		validatePayload: SendCommitRequest,
	) => {
		if (!validatePayload) {
			return formattedFormData as unknown as SendCommitRequest;
		}

		return deepMerge(
			validatePayload as unknown as Record<string, unknown>,
			formattedFormData,
		) as unknown as SendCommitRequest;
	};

	const handleFormSubmit = async (_data: Record<string, unknown>) => {
		if (!store.validatePayload || !_data) return;

		const formattedFormData = formatFieldsToDeeplyNested(_data);

		const updatedPayload = addAdditionalFieldsToValidatePayload(
			formattedFormData,
			store.validatePayload,
		);

		rampsActions.setValidatePayload(updatedPayload);

		const validationResponse = await apiClient.sendCommit.validateTransaction(
			store.transactionId,
			updatedPayload,
		);

		rampsActions.setTransactionResponse(validationResponse);

		if (validationResponse.readyForCommit) {
			navigate(
				navigateWithRampsState(search, {
					to: "/ramps/review",
					search: { step: "review" },
				}),
			);
		} else {
			// Handle validation failure
			if (validationResponse.errors && validationResponse.errors.length > 0) {
				const firstError = validationResponse.errors[0];

				// Show user-friendly error message
				toast.error(
					firstError.message ||
						t`Validation failed. Please check your information.`,
					{ header: t`Validation Error` },
				);
			} else {
				// Generic error if no specific error details
				toast.error(
					t`Unable to validate transaction. Please check your information and try again.`,
					{ header: t`Validation Failed` },
				);
			}
		}
	};

	return (
		<div className="flex flex-col min-h-screen max-w-screen-sm mx-auto bg-ramps-background-base">
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="w-8 h-8 flex items-center justify-center hover:opacity-80 transition-opacity"
					aria-label="Back"
				>
					<ArrowLeft className="w-6 h-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="px-4 mb-8">
				<Progress
					value={95}
					className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex-1 px-4 flex flex-col">
				<div className="text-left mb-8">
					<RampsText variant="title-lg-bold" color="primary" className="mb-2">
						{t`Additional Information Required`}
					</RampsText>
					<div className="text-sm text-muted-foreground">
						{t`Please provide the following additional information to complete your transaction.`}
					</div>
				</div>

				{offendingFields.length === 0 ? (
					<div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
						<RampsText variant="body-default" color="secondary">
							{t`Loading additional fields...`}
						</RampsText>
					</div>
				) : (
					<DynamicForm
						fields={offendingFields}
						onSubmit={handleFormSubmit}
						className="bg-card"
					/>
				)}
			</div>
		</div>
	);
}

export const Route = createFileRoute("/ramps/additional-info")({
	component: AdditionalInfoPage,
});
